import { useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Fragment } from 'react'
import { useMsal } from '@azure/msal-react'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'

interface Props {
  isOpen: boolean
  onClose: () => void
  tenantId: string
  onSuccess: () => void
}

export default function BaselineRefreshDialog({ isOpen, onClose, tenantId, onSuccess }: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const { accounts } = useMsal()

  const handleRefresh = async () => {
    if (!acknowledged) {
      toast.error('Please confirm that you understand the consequences')
      return
    }

    // Identify the requester for the audit log. MSAL's `account.username`
    // is the UPN/email for AAD-authenticated users — matches the convention
    // used by useSecurityBrief / useComplianceBrief / useIdentityBrief and
    // by what the Container's RequestedBy column expects (NVARCHAR up to
    // 200 chars). Fall back to display name, then a generic placeholder
    // only as a last resort, so we never block the action on a missing
    // identity but also never silently log 'Current User'.
    const account = accounts[0]
    const requestedBy =
      account?.username ?? account?.name ?? 'unknown-user'

    setIsLoading(true)
    try {
      await api.refreshBaseline(tenantId, requestedBy)

      toast.success('Baseline refresh started successfully')
      onSuccess()
      onClose()
    } catch (error: any) {
      // Handle 409 conflict specifically
      if (error.response?.status === 409) {
        const message = error.response?.data?.message || 'Another scan or baseline refresh is already in progress for this tenant. Please wait for it to complete.'
        toast.error(message, { duration: 6000 })
      } else {
        toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to start baseline refresh')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setAcknowledged(false)
      onClose()
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" aria-hidden="true" />
                  </div>
                  <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                    Refresh Baseline Warning
                  </Dialog.Title>
                </div>

                <div className="mt-4 space-y-4">
                  <p className="text-sm text-gray-600">
                    This will capture your current Microsoft 365 configuration as the new baseline.
                  </p>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-sm text-yellow-800">
                      <strong>Important:</strong> All existing drifts that match the new baseline will be automatically 
                      marked as <span className="font-medium">Remediated</span> and removed from your active drift list.
                    </p>
                  </div>
                  
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">
                      <strong>Warning:</strong> This action cannot be undone.
                    </p>
                  </div>

                  <div className="flex items-start space-x-3">
                    <input
                      id="acknowledge"
                      type="checkbox"
                      checked={acknowledged}
                      onChange={(e) => setAcknowledged(e.target.checked)}
                      disabled={isLoading}
                      className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="acknowledge" className="text-sm text-gray-700">
                      I understand the consequences and want to proceed with the baseline refresh
                    </label>
                  </div>
                </div>

                <div className="mt-6 flex space-x-3 justify-end">
                  <button
                    type="button"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleRefresh}
                    disabled={isLoading || !acknowledged}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      'Refresh Baseline'
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}